import * as React from 'react';
import { RefundServices } from '../../types';
import { getTotalPrice } from '@shared/lib/money';
import { Money as MoneyType } from '../../../types';
import Money from '../../../Money';
import { useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import {
	FlightFragment,
	IGdsServiceInfo,
	PassengerFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	SegmentFragment,
	CheckinSegmentFragment
} from '@websky/graphql';
import { getTravellerName } from '../../../utils';
import { flattenAllowedSegments } from '../../utils';

export interface Props {
	passengers: PassengerFragment[];
	services: RefundServices;
	serviceType?: OrderAdditionalServiceGdsServiceServiceType;
	flight: FlightFragment | CheckinSegmentFragment[];
	getServiceName?: (
		service: IGdsServiceInfo,
		productId: string,
		passengerId: string,
		count: number
	) => React.ReactNode;
}

interface PassengerService {
	serviceId: string;
	name: React.ReactNode;
	count: number;
	totalRefund: MoneyType;
}

interface PassengerServices {
	passengerId: string;
	passengerName: string;
	services: PassengerService[];
}

const findFlightSegment = (
	flight: FlightFragment | CheckinSegmentFragment[],
	segmentId: string
): CheckinSegmentFragment | SegmentFragment => {
	if (Array.isArray(flight)) {
		// checkin segments array
		return flight.find(segment => segment.id === segmentId);
	}

	return flight.segments.find(segment => segment.segment.id === segmentId)?.segment;
};

// Сегменты берём из allowedSegments сервиса (снапшот на момент расчёта рефанда), а не из
// traveller.services.gdsServices: при изменении цены бэкенд дублирует сервис с новым id
// продукта, и поиск по productId среди актуальных сервисов пассажира может не найти совпадение.
const getDefaultServiceName = (
	flight: FlightFragment | CheckinSegmentFragment[],
	service: IGdsServiceInfo,
	count: number
) => {
	const segments = flattenAllowedSegments(service.allowedSegments)
		.map(segmentId => findFlightSegment(flight, segmentId))
		.filter(Boolean)
		.map(segment => `${segment.departure.airport.iata} - ${segment.arrival.airport.iata}`);

	return `${segments.join(',')}, ${count} x ${service.name}`;
};

const CommonContent: React.FC<Props> = ({ passengers, services, getServiceName, flight }) => {
	const theme = useTheme('RefundSummary').CommonContent;
	const { t } = useTranslation('Exchange');

	const passengersServices = React.useMemo(() => {
		const passengersServices: PassengerServices[] = [];

		Object.keys(services).forEach(passengerId => {
			const passenger = passengers.find(passenger => passenger.id === passengerId);

			const passengerServices: PassengerService[] = [];

			Object.keys(services[passengerId]).forEach(emd => {
				Object.keys(services[passengerId][emd].services).forEach(serviceId => {
					const service = services[passengerId][emd].services[serviceId];

					passengerServices.push({
						serviceId,
						name: getServiceName
							? getServiceName(
									service.service,
									service.products[0]?.product?.id,
									passengerId,
									service.products.length
							  )
							: getDefaultServiceName(flight, service.service, service.products.length),
						count: service.products.length,
						totalRefund: service.sumToRefund
					});
				});
			});

			passengersServices.push({
				passengerId,
				passengerName: getTravellerName(passenger),
				services: passengerServices
			});
		});

		return passengersServices;
	}, [passengers, services]);

	const totalSumToRefund = React.useMemo(() => {
		const refunds: MoneyType[] = [];

		passengersServices.forEach(passengerService => {
			passengerService.services.forEach(service => {
				refunds.push(service.totalRefund);
			});
		});

		return getTotalPrice(refunds, refund => refund);
	}, [passengersServices]);

	return (
		<div className={theme.content}>
			<div className={theme.services}>
				{passengersServices.map(passengerServices => {
					return (
						<div className={theme.passenger} key={passengerServices.passengerId}>
							<div className={theme.passengerName}>{passengerServices.passengerName}</div>

							<div>
								{passengerServices.services.map(service => {
									return (
										<div className={theme.service} key={service.serviceId}>
											<div className={theme.name}>{service.name}</div>

											<div>
												<Money moneyClassName={theme.price} money={service.totalRefund} />
											</div>
										</div>
									);
								})}
							</div>
						</div>
					);
				})}
			</div>

			<div className={theme.totalRefund}>
				{t('Refund')}: <Money money={totalSumToRefund} />
			</div>
		</div>
	);
};

export default CommonContent;
